import { Injectable } from "@angular/core";
import { DatesDiffPipe, MILLISECONDS_IN_SECOND, RxjsUtils } from "@dtm-frontend/shared/utils";
import { HashMap, TranslocoService } from "@jsverse/transloco";
import MessageFormat from "@messageformat/core";
import { Observable, of } from "rxjs";
import { map, startWith, switchMap } from "rxjs/operators";
import { LANGUAGE_CONFIGURATION, LanguageCode, LanguageDefinition } from "../i18n.models";
import { SYSTEM_TRANSLATION_SCOPE } from "../shared-i18n.tokens";

const DURATION_FORMAT_REGEX = /{{(\w+?)\|duration}}/;
const MISSING_IF_EMPTY_REGEX = /{{(\w+?)\s?\|\s?missingIfEmpty(?::['"](.+?)['"])?}}/g;
const DEFAULT_MISSING_KEY = "dtmUiI18n.missing";
const MISSING_FALLBACKS: Record<LanguageCode, string> = {
    pl: "brak",
    en: "missing",
};
const OVERRIDDEN_TRANSLATION_PREFIX = "dtmUiI18n.overriddenSystemTranslationsByKey";
const OVERRIDDEN_TRANSLATION_KEYS: string[] = [];

@Injectable()
export class TranslationHelperService {
    public readonly datePickerPlaceholder$ = this.transloco.events$.pipe(
        map((event) => event.payload.langName),
        RxjsUtils.filterFalsy(),
        startWith(undefined),
        map((activeLang) => {
            activeLang = activeLang ?? this.transloco.getActiveLang();
            const languageDefinition =
                LANGUAGE_CONFIGURATION.languageDefinitions.find((entry: LanguageDefinition) => entry.alpha2Code === activeLang) ?? null;

            if (languageDefinition) {
                return languageDefinition.dateFormatHint;
            }

            return "";
        })
    );

    constructor(private readonly transloco: TranslocoService) {}

    public waitForTranslation(key: string, params?: Record<string, unknown>): Observable<string> {
        return this.transloco.selectTranslate<string>(key, params).pipe(
            switchMap((existingTranslationOrKey: string) => {
                if (existingTranslationOrKey !== key) {
                    return of(existingTranslationOrKey);
                }

                return this.transloco.events$.pipe(
                    map(() => {
                        const translationOrKey = this.transloco.translate<string>(key, params);

                        return translationOrKey === key ? null : translationOrKey;
                    }),
                    RxjsUtils.filterFalsy()
                );
            })
        );
    }

    public getActiveLocale() {
        return LANGUAGE_CONFIGURATION.localeMapping[this.transloco.getActiveLang()];
    }

    public selectTranslation(key: string, defaultValue: string, params?: HashMap) {
        return this.transloco.selectTranslate<string>(key, params).pipe(
            switchMap((existingTranslationOrKey: string) => {
                if (existingTranslationOrKey !== key) {
                    return of(existingTranslationOrKey);
                }

                return this.transloco.events$.pipe(
                    map(() => {
                        const translationOrKey = this.transloco.translate<string>(key);

                        return translationOrKey === key ? defaultValue : translationOrKey;
                    })
                );
            })
        );
    }

    public tryTranslate(key: string, defaultValue?: string, params?: HashMap) {
        const translation = this.transloco.translate(key, params);

        return translation === key ? defaultValue : translation;
    }

    public getSystemTranslation(key: string, params?: Record<string, unknown>): string {
        const systemTranslationKey = this.getSystemTranslationKey(key);

        const translationMap = this.transloco.getTranslation(this.transloco.getActiveLang());
        const translationTemplateValue: string = translationMap[systemTranslationKey];

        const missingIfEmptyMatchesArray = translationTemplateValue
            ? Array.from(translationTemplateValue.matchAll(MISSING_IF_EMPTY_REGEX))
            : [];
        const durationFormatMatch = translationTemplateValue?.match(DURATION_FORMAT_REGEX);

        if (missingIfEmptyMatchesArray.length === 0 && !durationFormatMatch?.[0]) {
            return this.transloco.translate(systemTranslationKey, params);
        }

        let processedParams = { ...params };
        let updatedTranslationTemplateValue = translationTemplateValue;

        // NOTE: We replace each occurrence with a unique variable name to avoid affecting
        // other occurrences of the same variable without the modifier
        if (missingIfEmptyMatchesArray.length > 0) {
            let replacementIndex = 0;

            missingIfEmptyMatchesArray.forEach((match) => {
                const [fullMatch, argumentName, customKey] = match;
                const argumentValue = processedParams?.[argumentName];

                if (this.isEmptyValue(argumentValue)) {
                    const uniqueVarName = `${argumentName}_missingIfEmpty_${replacementIndex}`;
                    processedParams[uniqueVarName] = this.resolveMissingValue(customKey);
                    updatedTranslationTemplateValue = updatedTranslationTemplateValue.replace(fullMatch, `{{${uniqueVarName}}}`);

                    replacementIndex++;
                } else {
                    updatedTranslationTemplateValue = updatedTranslationTemplateValue.replace(fullMatch, `{{${argumentName}}}`);
                }
            });
        }

        if (durationFormatMatch?.[0]) {
            const argumentName = durationFormatMatch[1];
            const argumentValue = (processedParams?.[argumentName] as number) ?? 0;
            const newArgValue = new DatesDiffPipe(this.transloco).transform(
                new Date(),
                new Date(new Date().getTime() + Number(argumentValue) * MILLISECONDS_IN_SECOND)
            );
            processedParams = { ...processedParams, [argumentName]: newArgValue };
        }

        updatedTranslationTemplateValue = updatedTranslationTemplateValue
            .replace(new RegExp(DURATION_FORMAT_REGEX, "g"), "{$1}")
            .replace(/{{(.+?)}}/g, "{$1}");

        const templateVariables = new Set<string>();
        const variableRegex = /{(\w+)}/g;
        let variableMatch: RegExpExecArray | null;
        while ((variableMatch = variableRegex.exec(updatedTranslationTemplateValue)) !== null) {
            templateVariables.add(variableMatch[1]);
        }

        const finalParams: Record<string, unknown> = {};

        Object.entries(processedParams).forEach(([paramKey, value]) => {
            finalParams[paramKey] = value ?? "";
        });

        for (const variableName of templateVariables) {
            if (!(variableName in finalParams)) {
                finalParams[variableName] = "";
            }
        }

        return new MessageFormat(null).compile(updatedTranslationTemplateValue)(finalParams);
    }

    public getSystemTranslationKey(key: string) {
        return OVERRIDDEN_TRANSLATION_KEYS.includes(key) ? `${OVERRIDDEN_TRANSLATION_PREFIX}.${key}` : `${SYSTEM_TRANSLATION_SCOPE}.${key}`;
    }

    private isEmptyValue(value: unknown): boolean {
        if (value === null || value === undefined) {
            return true;
        }

        if (typeof value !== "string") {
            return false;
        }

        const trimmedValue = value.trim();

        if (trimmedValue === "") {
            return true;
        }

        const lowerCaseValue = trimmedValue.toLowerCase();
        if (lowerCaseValue === "null" || lowerCaseValue === "undefined") {
            return true;
        }

        const dashes = ["-", "–", "—", "‐", "‑", "―"];

        return dashes.includes(trimmedValue);
    }

    private resolveMissingValue(customKey?: string): string {
        let keyToTranslate = DEFAULT_MISSING_KEY;
        if (customKey !== undefined) {
            keyToTranslate = this.getSystemTranslationKey(customKey);
        }
        const translation = this.transloco.translate(keyToTranslate);

        if (translation !== keyToTranslate) {
            return translation;
        }

        const activeLang = this.transloco.getActiveLang() as LanguageCode;

        return MISSING_FALLBACKS[activeLang] ?? MISSING_FALLBACKS.pl;
    }
}
